import type {IElementData, IElementsMap, INetsMap, NodeUid, VertexUid} from "@buildwithflux/models";
import {useShallow} from "zustand/react/shallow";

import {getAllBakedNodes} from "modules/stores/pcb/utils";

import {useFluxServices} from "../../../../injection/hooks";
import {select} from "../selectors";
import type {ISelectionUidsState} from "../selectors/selection/derived";
import {
    computeAllSelectedPcbLayoutNodeUids,
    computeAllSelectedPcbLayoutNodeUidsCount,
    computeSelectionUidsState,
} from "../selectors/selection/derived";

export type {ISelectionUidsState} from "../selectors/selection/derived";
export type {SelectionUidsState} from "../selectors/selection/derived";

/**
 * Hook to get all selected object UIDs
 */
export const useSelectedObjectUids = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.selection.allUids));
};

/**
 * Hook to check if any object is selected
 */
export const useHasSelection = (): boolean => {
    return useFluxServices().useDocumentStore(select.selection.hasSelection);
};

/**
 * Hook to get count of selected objects
 */
export const useSelectionCount = (): number => {
    return useFluxServices().useDocumentStore(select.selection.count);
};

/**
 * Hook to get count of selected element UIDs (filtered, excludes branch points)
 * Returns a primitive number to avoid useShallow overhead - use this when you only need the count
 */
export const useSelectedElementUidsCount = (): number => {
    return useFluxServices().useDocumentStore(select.selection.elementUidsCount);
};

/**
 * Hook to get count of selected locked element UIDs
 * Returns a primitive number to avoid useShallow overhead - use this when you only need the count
 */
export const useLockedSelectedElementUidsCount = (): number => {
    return useFluxServices().useDocumentStore(select.selection.lockedElementUidsCount);
};

/**
 * Hook to get count of selected symbol UIDs
 * Returns a primitive number to avoid useShallow overhead - use this when you only need the count
 */
export const useSelectedSymbolUidsCount = (): number => {
    return useFluxServices().useDocumentStore(select.selection.symbolUidsCount);
};

/**
 * Hook to get count of selected net UIDs
 * Returns a primitive number to avoid useShallow overhead - use this when you only need the count
 */
export const useSelectedNetUidsCount = (): number => {
    return useFluxServices().useDocumentStore(select.selection.netUidsCount);
};

/**
 * Hook to get total count of all selected object UIDs
 * Returns a primitive number - use this when you only need the total count
 */
export const useSelectedAllCount = (): number => {
    return useFluxServices().useDocumentStore(select.selection.allCount);
};

/**
 * Hook to get count of selected unbaked PCB layout node UIDs
 * Returns a primitive number to avoid useShallow overhead
 */
export const useSelectedUnbakedPcbLayoutNodeUidsCount = (): number => {
    return useFluxServices().useDocumentStore(select.selection.unbakedPcbLayoutNodeUidsCount);
};

/**
 * Hook to get count of selected PCB layout rule set UIDs
 * Returns a primitive number to avoid useShallow overhead
 */
export const useSelectedPcbLayoutRuleSetUidsCount = (): number => {
    return useFluxServices().useDocumentStore(select.selection.pcbLayoutRuleSetUidsCount);
};

/**
 * Hook to get count of selected route UIDs
 * Returns a primitive number to avoid useShallow overhead
 */
export const useSelectedRouteUidsCount = (): number => {
    return useFluxServices().useDocumentStore(select.selection.routeUidsCount);
};

/**
 * Hook to get the single selected element UID when exactly one element is selected.
 * Returns undefined otherwise. Primitive return avoids useShallow overhead.
 */
export const useSingleSelectedElementUid = (): string | undefined => {
    return useFluxServices().useDocumentStore(select.selection.singleSelectedElementUid);
};

/**
 * Hook to get the single selected symbol UID when exactly one symbol is selected.
 * Returns undefined otherwise. Primitive return avoids useShallow overhead.
 */
export const useSingleSelectedSymbolUid = (): string | undefined => {
    return useFluxServices().useDocumentStore(select.selection.singleSelectedSymbolUid);
};

/**
 * Hook to check if only elements (and their symbols) are selected.
 * Returns a boolean - primitive return avoids useShallow overhead.
 */
export const useOnlyElementsSelected = (): boolean => {
    return useFluxServices().useDocumentStore(select.selection.onlyElementsSelected);
};

/**
 * Hook to check if any selected UID is unknown (not found in any store collection).
 * Returns a boolean - use this to determine if the multi-selection panel should show.
 * NOTE: Only checks store-level collections (elements, nets, routes, etc.).
 * Scene-level subjects (route segments, symbols) should be checked separately.
 */
export const useHasUnknownSelectionUid = (): boolean => {
    return useFluxServices().useDocumentStore(select.selection.hasUnknownSelectionUid);
};

/**
 * Hook to check if any selected element lacks a Manufacturer Part Number property value.
 * Returns a boolean primitive - no useShallow needed.
 */
export const useHasSelectedElementWithoutMpn = (): boolean => {
    return useFluxServices().useDocumentStore(select.selection.hasSelectedElementWithoutMpn);
};

/**
 * Hook to get selected element UIDs (filtered, excludes branch points) - memoized
 */
export const useSelectedElementUids = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.selection.elementUids));
};

/**
 * Hook to get selected elements (filtered, excludes branch points) - memoized
 */
export const useSelectedElements = (): Array<IElementsMap[string]> => {
    return useFluxServices().useDocumentStore(useShallow(select.selection.elements));
};

/**
 * Hook to get selected symbol UIDs - memoized
 */
export const useSelectedSymbolUids = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.selection.symbolUids));
};

/**
 * Hook to get selected net UIDs - memoized
 */
export const useSelectedNetUids = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.selection.netUids));
};

/**
 * Hook to get selected nets - memoized
 */
export const useSelectedNets = (): Array<INetsMap[string]> => {
    return useFluxServices().useDocumentStore(useShallow(select.selection.nets));
};

/**
 * Hook to get selected vertex UIDs - memoized
 */
export const useSelectedVertexUids = (): VertexUid[] => {
    return useFluxServices().useDocumentStore(useShallow(select.selection.vertexUids));
};

/**
 * Hook to get selected route UIDs - memoized
 */
export const useSelectedRouteUids = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.selection.routeUids));
};

/**
 * Hook to get selected comment thread UIDs - memoized
 */
export const useSelectedCommentThreadUids = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.selection.commentThreadUids));
};

/**
 * Hook to get ALL selected PCB layout node UIDs including both baked and unbaked nodes.
 * Baked nodes come from documentService.snapshot().pcbLayoutNodes (e.g. silk text inside components).
 * Unbaked nodes come from document.pcbLayoutNodes.
 *
 * Use this for selection-dependent UI (bounding boxes, context menus, delete, etc.)
 * where baked nodes like silk text must be handled.
 */
export const useAllSelectedPcbLayoutNodeUids = (): NodeUid[] => {
    const {documentService, useDocumentStore} = useFluxServices();
    const allBakedNodes = getAllBakedNodes(documentService);

    return useDocumentStore(
        useShallow((state) => {
            return computeAllSelectedPcbLayoutNodeUids(state.selectedObjectUids, allBakedNodes, state.pcbLayoutNodes);
        }),
    );
};

/**
 * Hook to get count of ALL selected PCB layout node UIDs including both baked and unbaked nodes.
 * Returns a primitive number to minimize re-renders.
 */
export const useAllSelectedPcbLayoutNodeUidsCount = (): number => {
    const {documentService, useDocumentStore} = useFluxServices();
    const allBakedNodes = getAllBakedNodes(documentService);

    return useDocumentStore((state) => {
        return computeAllSelectedPcbLayoutNodeUidsCount(state.selectedObjectUids, allBakedNodes, state.pcbLayoutNodes);
    });
};

/**
 * Hook to get selected PCB layout rule set UIDs - memoized
 */
export const useSelectedPcbLayoutRuleSetUids = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.selection.pcbLayoutRuleSetUids));
};

/**
 * Hook to get locked selected element UIDs - memoized
 */
export const useLockedSelectedElementUids = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.selection.lockedElementUids));
};

/**
 * Hook to get selected element and net UIDs combined - memoized
 */
export const useSelectedElementAndNetUids = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.selection.elementAndNetUids));
};

/**
 * Hook to check if any subject is selected
 * This is an alias for useHasSelection for backwards compatibility
 */
export const useIsAnySubjectSelected = (): boolean => {
    return useFluxServices().useDocumentStore(select.selection.hasSelection);
};

/**
 * Hook to get labels (designators) for selected objects across elements, nets, and pcbLayoutNodes
 */
export const useSelectedDesignators = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.selection.selectedDesignators));
};

/**
 * Hook to get selected elements that have a datasheet property value
 */
export const useSelectedElementsWithDatasheet = (): IElementData[] => {
    return useFluxServices().useDocumentStore(useShallow(select.selection.elementsWithDatasheet));
};

/**
 * Hook to get selected elements that do NOT have a datasheet property value
 */
export const useSelectedElementsWithoutDatasheet = (): IElementData[] => {
    return useFluxServices().useDocumentStore(useShallow(select.selection.elementsWithoutDatasheet));
};

/**
 * Hook to get selected elements that have a label AND a default property value with unit
 */
export const useSelectedElementsWithDefaultValue = (): IElementData[] => {
    return useFluxServices().useDocumentStore(useShallow(select.selection.elementsWithDefaultValue));
};

/**
 * Hook to get the shared label for multi-selection.
 * Returns "Mixed" if selected objects have different labels, undefined if nothing selected.
 */
export const useMultiSelectionLabel = (): string | undefined => {
    return useFluxServices().useDocumentStore(select.selection.multiSelectionLabel);
};

/**
 * Hook to check if an object (element or PCB node) is locked
 * Checks both direct lock state and inherited lock state for PCB nodes
 *
 * @param uid - The UID of the object to check
 * @returns true if the object is locked (directly or by inheritance), false otherwise
 */
export const useIsObjectLocked = (uid: string | undefined): boolean => {
    return useFluxServices().useDocumentStore(select.selection.isObjectLocked(uid));
};

/**
 * Hook to check if any of the selected objects are locked
 * Checks both elements and PCB nodes for direct and inherited locks
 *
 * @returns true if any selected object is locked, false otherwise
 */
export const useAnySelectedObjectLocked = (): boolean => {
    return useFluxServices().useDocumentStore(select.selection.anySelectedObjectLocked);
};

/**
 * Hook to get the complete selection UIDs state from the Zustand store
 * This is a combined hook that returns all selection-related UIDs in a single object,
 * matching the Redux selectSelectionUidsState selector interface.
 * NOTE: Uses shallow comparison to avoid unnecessary re-renders
 * NOTE: Checks both baked nodes (from documentService.snapshot().pcbLayoutNodes) and
 *       unbaked nodes (from document.pcbLayoutNodes) to handle silk text inside components
 */
export const useSelectionUidsState = (): ISelectionUidsState => {
    const {documentService, useDocumentStore} = useFluxServices();

    const allBakedNodes = getAllBakedNodes(documentService);

    return useDocumentStore(
        useShallow((state) => {
            return computeSelectionUidsState(
                state.selectedObjectUids || [],
                state.elements,
                state.nets,
                state.routeVertices,
                state.routes,
                state.comment_threads,
                state.pcbLayoutNodes,
                state.pcbLayoutRuleSets,
                allBakedNodes,
            );
        }),
    );
};
